import React, { useState, useEffect, useRef } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TableSortLabel,
    TextField,
    CircularProgress,
    Alert,
    Box,
    Button,
    Menu,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    IconButton,
    Tooltip,
    Typography
} from '@mui/material';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { callApi, fetchPresignUrl } from '../utils/apiHelper';
import { useAuthenticator } from '@aws-amplify/ui-react';

function JobStatusTable({ refreshTrigger = 0 }) {
    const [jobs, setJobs] = useState([]);
    const [orderBy, setOrderBy] = useState('LastUpdatedAt');
    const [order, setOrder] = useState('desc');
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [downloadLoading, setDownloadLoading] = useState({}); // State to manage loading for individual downloads
    const [dateFilterAnchorEl, setDateFilterAnchorEl] = useState(null); // For date filter popover
    const [selectedDateFilter, setSelectedDateFilter] = useState('90_days'); // Default to past 1 day
    const [customDays, setCustomDays] = useState(1); // For custom date range
    const [isCustomDateRangeDialogOpen, setIsCustomDateRangeDialogOpen] = useState(false); // For custom date range dialog
    const [htmlModalOpen, setHtmlModalOpen] = useState(false); // State for HTML modal
    const [htmlModalContent, setHtmlModalContent] = useState(''); // State for HTML modal content
    const hasInitializedRef = useRef(false);

    const { user } = useAuthenticator((context) => [context.user]);

    useEffect(() => {
        // Only fetch on mount (when refreshTrigger is 0) or when refreshTrigger changes
        if (refreshTrigger === 0) {
            // On mount - use ref to prevent double call
            if (!hasInitializedRef.current) {
                hasInitializedRef.current = true;
                fetchJobStatuses();
            }
        } else {
            // On refresh trigger - always fetch
            fetchJobStatuses();
        }
    }, [refreshTrigger]); // Only depend on refreshTrigger

    const fetchJobStatuses = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await callApi({
                user,
                path: '/ts-service-main/get_jobs',
                method: 'GET'
            });
            setJobs(response);
            // Fetch file URLs for all jobs after they are loaded
        } catch (error) {
            console.error('Error fetching job statuses:', error);
            setError(error.message || 'Failed to fetch job statuses');
        } finally {
            setIsLoading(false);
        }
    };

    // Function to generate file paths based on job details
    const generateFilePaths = (job) => {
        if (!job || !job.JobOutput) return [];

        // Split JobOutput into individual filenames
        const filenames = job.JobOutput.split(',');

        // Use JobOutputFolder if available, fallback to legacy path
        const folder = job.JobOutputFolder || `${job.JobType}/${job.JobID}`;
        return filenames.map(filename => ({
            filename,
            path: `${folder}/${filename.trim()}`
        }));
    };


    const handleDateFilterClick = (event) => {
        setDateFilterAnchorEl(event.currentTarget);
    };

    const handleDateFilterClose = () => {
        setDateFilterAnchorEl(null);
    };

    const handleDateFilterChange = (event) => {
        const value = event.target.value;
        setSelectedDateFilter(value);
        handleDateFilterClose();
        if (value === 'custom') {
            setIsCustomDateRangeDialogOpen(true);
        }
    };

    const handleCustomDaysChange = (event) => {
        const value = parseInt(event.target.value, 10);
        if (!isNaN(value) && value > 0) {
            setCustomDays(value);
        }
    };

    const handleCustomDateRangeDialogClose = () => {
        setIsCustomDateRangeDialogOpen(false);
    };

    const handleDownloadResult = async (job, viewHtml) => {
        if (!job || !job.JobOutput) return;

        setDownloadLoading(prev => ({ ...prev, [job.JobID]: true }));
        setError(null);

        try {
            const paths = generateFilePaths(job);
            const htmlFile = paths.find(file => file.filename.trim().endsWith('.html'));

            if (viewHtml && htmlFile) {
                const response = await fetchPresignUrl(user, htmlFile.path, 'download', true);
                if (response && response.presigned_url) {
                    // Fetch the HTML content and display in modal
                    const htmlResponse = await fetch(response.presigned_url);
                    const htmlContent = await htmlResponse.text();
                    setHtmlModalContent(htmlContent);
                    setHtmlModalOpen(true);
                } else {
                    setError('Presigned URL not found in the response.');
                }
            } else if (htmlFile) {
                const response = await fetchPresignUrl(user, htmlFile.path, 'download');
                if (response && response.presigned_url) {
                    window.open(response.presigned_url, '_blank', 'noopener noreferrer');
                } else {
                    setError('Presigned URL not found in the response.');
                }
            } else {
                setError('No HTML output file found for this job.');
            }
        } catch (error) {
            console.error('Error downloading result:', error);
            setError(error.message || 'Failed to download result.');
        } finally {
            setDownloadLoading(prev => ({ ...prev, [job.JobID]: false }));
        }
    };
    const handleSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const sortedJobs = [...jobs].sort((a, b) => {
        const compare = (a, b) => {
            if (a < b) return -1;
            if (a > b) return 1;
            return 0;
        };

        const modifier = order === 'asc' ? 1 : -1;
        return modifier * compare(a[orderBy], b[orderBy]);
    });

    const filteredJobs = sortedJobs.filter(job => {
        const jobDate = new Date(job.LastUpdatedAt);
        let startDate = new Date();

        if (selectedDateFilter === '1_day') {
            startDate.setDate(startDate.getDate() - 1);
        } else if (selectedDateFilter === '7_days') {
            startDate.setDate(startDate.getDate() - 7);
        } else if (selectedDateFilter === '14_days') {
            startDate.setDate(startDate.getDate() - 14);
        } else if (selectedDateFilter === '30_days') {
            startDate.setDate(startDate.getDate() - 30);
        } else if (selectedDateFilter === '90_days') {
            startDate.setDate(startDate.getDate() - 90);
        } else if (selectedDateFilter === 'custom') {
            startDate.setDate(startDate.getDate() - customDays);
        }

        const matchesSearchTerm = Object.values(job).some(value =>
            value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        );

        const matchesDateFilter = jobDate >= startDate;

        return matchesSearchTerm && matchesDateFilter;
    });

    const handleShowJobParams = (params) => {
        try {
            const beautifiedParams = JSON.stringify(params, null, 2);
            alert(`Job Parameters:\n${beautifiedParams}`);
        } catch (e) {
            alert(`Job Parameters:\n${params}`);
        }
    };

    // HTML Result Modal
    const handleCloseHtmlModal = () => {
        setHtmlModalOpen(false);
        setHtmlModalContent('');
    };

    return (
        <>
            <Paper sx={{ padding: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        marginBottom: 2
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={fetchJobStatuses}
                        disabled={isLoading}
                    >
                        {isLoading ? <CircularProgress size={24} /> : 'Fetch Jobs'}
                    </Button>

                    <TextField
                        label="Search Jobs"
                        variant="outlined"
                        sx={{ flexGrow: 1 }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                            aria-label="filter list"
                            onClick={handleDateFilterClick}
                            color="primary"
                        >
                            <FilterAltIcon />
                        </IconButton>
                        <Typography variant="body2" sx={{ ml: -1, fontWeight: 'bold', color: 'primary.main', whiteSpace: 'nowrap' }}>
                            {selectedDateFilter === 'custom' ? `${customDays}d` : selectedDateFilter.replace('_', ' ')}
                        </Typography>
                    </Box>
                    <Menu
                        anchorEl={dateFilterAnchorEl}
                        open={Boolean(dateFilterAnchorEl)}
                        onClose={handleDateFilterClose}
                    >
                        <FormControl component="fieldset" sx={{ padding: 2 }}>
                            <FormLabel component="legend">Filter by Date</FormLabel>
                            <RadioGroup
                                aria-label="date-filter"
                                name="date-filter-group"
                                value={selectedDateFilter}
                                onChange={handleDateFilterChange}
                            >
                                <FormControlLabel value="1_day" control={<Radio />} label="Past 1 Day" />
                                <FormControlLabel value="7_days" control={<Radio />} label="Past 7 Days" />
                                <FormControlLabel value="14_days" control={<Radio />} label="Past 14 Days" />
                                <FormControlLabel value="30_days" control={<Radio />} label="Past 30 Days" />                                <FormControlLabel value="90_days" control={<Radio />} label="Past 90 Days" />
                                <FormControlLabel value="custom" control={<Radio />} label="Custom Date Range" />
                            </RadioGroup>
                        </FormControl>
                    </Menu>
                </Box>

                <Dialog open={isCustomDateRangeDialogOpen} onClose={handleCustomDateRangeDialogClose}>
                    <DialogTitle>Custom Date Range</DialogTitle>
                    <DialogContent>
                        <TextField
                            label="Number of Days"
                            type="number"
                            value={customDays}
                            onChange={handleCustomDaysChange}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <CalendarTodayIcon />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mt: 2 }}
                            fullWidth
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCustomDateRangeDialogClose}>Cancel</Button>
                        <Button onClick={handleCustomDateRangeDialogClose} variant="contained">Apply</Button>
                    </DialogActions>
                </Dialog>

                {error && (
                    <Alert severity="error" sx={{ marginBottom: 2 }}>
                        {error}
                    </Alert>
                )}

                <TableContainer sx={{ maxHeight: 400 }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                {['JobID', 'LastUpdatedAt', 'JobType', 'JobLabel', 'JobStatus', 'Actions'].map((header) => (
                                    <TableCell key={header}>
                                        <TableSortLabel
                                            active={orderBy === header}
                                            direction={orderBy === header ? order : 'asc'}
                                            onClick={() => handleSort(header)}
                                        >
                                            {header}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredJobs.map((job) => {
                                const hasHtmlOutput = job.JobOutput && job.JobOutput.split(',').some(filename => filename.trim().endsWith('.html'));
                                const isDownloading = downloadLoading[job.JobID];

                                return (
                                    <TableRow key={job.JobID}>
                                        <TableCell>{job.JobID}</TableCell>
                                        <TableCell>
                                            {new Date(job.LastUpdatedAt).toLocaleString('en-US', {
                                                year: 'numeric',
                                                month: '2-digit',
                                                day: '2-digit',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                second: '2-digit',
                                                hour12: false
                                            })}
                                        </TableCell>
                                        <TableCell>{job.JobType}</TableCell>
                                        <TableCell>
                                            {job.JobLabel || ''}
                                            {job.JobParams && (
                                                <Tooltip title="Show Job Parameters">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleShowJobParams(job.JobParams)}
                                                        aria-label="show job parameters"
                                                    >
                                                        <InfoOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell>{job.JobStatus}</TableCell>
                                        <TableCell>
                                            {hasHtmlOutput && (
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={() => handleDownloadResult(job, true)}
                                                    disabled={isDownloading}
                                                    startIcon={isDownloading ? <CircularProgress size={20} /> : <QueryStatsIcon />}
                                                    sx={{ mt: 1, mb: 1, height: '40px', padding: 0, minWidth: 'auto', width: '220px', justifyContent: 'center' }}
                                                >
                                                    {isDownloading ? 'Loading...' : 'View Result'}
                                                </Button>
                                            )}
                                            {hasHtmlOutput && (
                                                <Button
                                                    variant="text"
                                                    color="primary"
                                                    onClick={() => handleDownloadResult(job)}
                                                    disabled={isDownloading}
                                                    startIcon={isDownloading ? <CircularProgress size={20} /> : <CloudDownloadIcon />}
                                                    sx={{ mt: 1, mb: 1, height: '40px', padding: 0, minWidth: 'auto', width: '40px', justifyContent: 'right' }}
                                                >
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* HTML Result Modal */}
            <Dialog
                open={htmlModalOpen}
                onClose={handleCloseHtmlModal}
                maxWidth={false} // Disable maxWidth to use custom width
                sx={{
                    '& .MuiDialog-paper': {
                        width: '80%',
                        maxWidth: '80%',
                        height: '80%',
                    }
                }}
            >
                <DialogTitle>Job Result</DialogTitle>
                <DialogContent dividers>
                    <iframe
                        srcDoc={htmlModalContent}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title="Job Result"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseHtmlModal} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default JobStatusTable;